import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { StartupService } from '../common/services/startup.service';
import { session } from '../common/session/session';


interface UtagData {
  applicationID?: string;
  channel?: string;
  segment?: string;
  pageIdentifier?: string;
  sectionFamily?: string;
  sectionCategory?: string;
  sectionName?: string;
  uid?: string;
  pageName?: string;
  tealium_event?: string;
  [key: string]: string | undefined; // Allow additional dynamic properties
}

@Injectable({
  providedIn: "root",
})
export class TealiumScriptService {
  private scriptConf;

  constructor(
    @Inject(DOCUMENT) private readonly document,
    private readonly startup: StartupService
  ) {
    this.setData();
  }

  private setData() {
    const isCahoot = this.startup.getIsCahoot();
    this.scriptConf = {
      applicationID: isCahoot ? "CAHOOT_CONTACT_US" : "RETAIL_CONTACT_US",
      channel: "olb",
      segment: isCahoot ? "cahoot" : "retail",
      pageIdentifier: "contact-us",
      sectionFamily: "olb",
      sectionCategory: "products",
      sectionName: "customer",
      uid: session.uid ?? '',
    };

    if (this.startup.tealiumLogonUrl != null) {
      this.scriptConf.src = this.startup.tealiumLogonUrl;
    }

    // Set up utag_data before loading the script
    this.setupUtagData();
  }

  private setupUtagData(): void {
    // Initialize utag_data on window object before loading utag.js
    window['utag_data'] = {
      applicationID: this.scriptConf.applicationID,
      channel: this.scriptConf.channel,
      segment: this.scriptConf.segment,
      pageIdentifier: this.scriptConf.pageIdentifier,
      sectionFamily: this.scriptConf.sectionFamily,
      sectionCategory: this.scriptConf.sectionCategory,
      sectionName: this.scriptConf.sectionName,
      uid: this.scriptConf.uid,
      pageName: '/contact-us',
      tealium_event: 'view'
    };
  }

  updateUtagData(data:UtagData): void {
    if (window['utag_data']) {
      // Merge new data with existing utag_data
      Object.assign(window['utag_data'], data);
    } else {
      console.info('utag_data is not available');
    }
  }

  getUtagData(): UtagData {
    return window['utag_data'] || {};
  }

  trackEvent(eventName: string, data = {}): void {
    if (window['utag'] && typeof window['utag'].link === 'function') {
      const eventData = { tealium_event: eventName, ...data };
      window['utag'].link(eventData);
    }
  }

  initialize(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.loadHeader()
        .then(() => {
          resolve("Tealium has been applied");
        })
        .catch(() => {
          reject(new Error("Tealium has not been applied"));
        });
    });
  }

  public loadHeader(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (this.scriptConf.loaded) {
        resolve("already loaded");
        return;
      }

      this.setupUtagData();

      const script = this.document.createElement("script");
      script.src = this.scriptConf.src;

      const onScriptLoad = () => {
        this.scriptConf.loaded = true;
        resolve("loaded");
      };

      if (script.readyState) {
        script.onreadystatechange = () => {
          if (
            script.readyState === "loaded" ||
            script.readyState === "complete"
          ) {
            script.onreadystatechange = null;
            onScriptLoad();
          }
        };
      } else {
        script.onload = onScriptLoad;
      }
      script.onerror = () => {
        this.scriptConf.loaded = false;
        reject(new Error("not loaded"));
      };
      this.document.getElementsByTagName("head")[0].appendChild(script);
    });
  }

  updateUtagAndTrackTealiumEvent(eventName: string, pageName: string): void {
    const data = { pageName, pageIdentifier: eventName };
    this.updateUtagData(data);
    this.trackEvent(eventName, data);
  }

}
